import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Button, CircularProgress, IconButton, Stack, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LinkIcon from '@mui/icons-material/Link';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import useApi from 'hooks/useApi';
import MediaViewer, { ViewerItem } from 'components/MediaViewer';
import { AnnouncementAttachment } from 'types/auth';
import useAttachmentUrls from '../hooks/useAttachmentUrls';

// The server re-derives this from the mime prefix on every read; we compute it locally
// so a just-uploaded file previews correctly before any round trip.
const mediaTypeFor = (mimeType?: string): AnnouncementAttachment['media_type'] => {
  if (mimeType?.startsWith('image/')) return 'image';
  if (mimeType?.startsWith('video/')) return 'video';
  return 'file';
};

type MediaKind = AnnouncementAttachment['media_type'];

// Worked out from the URL rather than asked for, so a direct .mp4 or .png still opens in
// the lightbox while the author only has to supply a label. Anything unrecognised — most
// CDN and share links, which carry no extension — becomes a link card that opens in a
// new tab, which is the safe outcome either way.
const kindFromUrl = (url: string): MediaKind => {
  let path = url;
  try {
    path = new URL(url).pathname;
  } catch {
    // Not parseable yet — the author is probably mid-paste. Fall through on the raw text.
  }
  const extension = path.split('.').pop()?.toLowerCase() ?? '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif'].includes(extension)) return 'image';
  if (['mp4', 'webm', 'ogg', 'ogv', 'mov', 'm4v'].includes(extension)) return 'video';
  return 'file';
};

// The last path segment reads better than the whole URL on a 92px tile; hostname is the
// fallback for links that end in a slash.
const nameFromUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.pathname.split('/').filter(Boolean).pop() || parsed.hostname;
  } catch {
    return url;
  }
};

interface AttachmentUploaderProps {
  value: AnnouncementAttachment[];
  // Takes an updater rather than a plain list: a second drop landing while the first
  // batch is still uploading would otherwise append to a stale `value` and silently
  // discard the earlier files.
  onChange: Dispatch<SetStateAction<AnnouncementAttachment[]>>;
  disabled?: boolean;
  // Narrow what the file picker offers. Defaults to everything in ACCEPTED_TYPES —
  // a caller passes a subset when the surface that renders the attachment can only
  // handle some of them (the REDIRECT home step, for one). `hint` is the caption
  // under the dropzone; it does not restrict anything, so keep it in step with
  // whatever `accept` is passed.
  accept?: Record<string, string[]>;
  hint?: string;
}

const TILE = 92;

// Images and video preview in the lightbox; everything below lands as media_type 'file'
// and renders as a download card. Extensions are listed alongside each mime type because
// the OS file picker filters on them, and some browsers report an empty type for .csv.
// Add a line here to allow another format — nothing server-side restricts the type.
const ACCEPTED_TYPES: Record<string, string[]> = {
  'image/*': [],
  'video/*': [],
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
  'text/csv': ['.csv'],
  'text/plain': ['.txt']
};

/**
 * Drag-and-drop attachment picker for the announcement form.
 *
 * Files go straight to the shared `utility/file/upload` endpoint via `uploadAsset` —
 * there is no announcement-specific upload route. The returned metadata is held in form
 * state and only persisted when the announcement itself is saved, so a file dropped and
 * then removed simply leaves an orphaned object in storage.
 *
 * `uploadAsset` exposes no progress events, so each pending file gets an indeterminate
 * spinner rather than a percentage.
 */
const AttachmentUploader = ({
  value,
  onChange,
  disabled = false,
  accept = ACCEPTED_TYPES,
  hint = 'or click to browse — PDF, Word, Excel, PowerPoint, CSV'
}: AttachmentUploaderProps) => {
  const { uploadAsset, showToast } = useApi();
  const [pending, setPending] = useState<string[]>([]);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkLabel, setLinkLabel] = useState('');
  // The URL returned at upload time expires after 600s, so a slowly-filled form can end
  // up with dead thumbnails. This re-signs from file_path when that happens.
  const previewSrc = useAttachmentUrls(value);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return;
      setPending((prev) => [...prev, ...acceptedFiles.map((file) => file.name)]);

      const uploaded: AnnouncementAttachment[] = [];
      for (const file of acceptedFiles) {
        try {
          const response = await uploadAsset(file, 'announcements');
          const data = response?.data;
          if (!data?.file_path) {
            showToast(`Could not upload ${file.name}`, 'error');
            continue;
          }
          uploaded.push({
            file_name: data.file_name ?? file.name,
            file_path: data.file_path,
            mime_type: data.mime_type ?? file.type,
            public_url: data.public_url ?? null,
            file_size: data.file_size ?? file.size,
            media_type: mediaTypeFor(data.mime_type ?? file.type),
            // Short-lived signed URL, used for the preview only. Never sent back on save.
            url: data.url ?? null
          });
        } catch (error: any) {
          showToast(error?.message || `Could not upload ${file.name}`, 'error');
        } finally {
          setPending((prev) => {
            const next = [...prev];
            const index = next.indexOf(file.name);
            if (index > -1) next.splice(index, 1);
            return next;
          });
        }
      }

      if (uploaded.length) onChange((prev) => [...prev, ...uploaded]);
    },
    [uploadAsset, showToast, onChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    accept
  });

  const removeAt = (index: number) => onChange((prev) => prev.filter((_item, i) => i !== index));

  const addLink = () => {
    const url = linkUrl.trim();
    if (!url) return;
    // http/https only — these end up in an <img src> and in window.open, so javascript:
    // and data: URLs must not get through.
    if (!/^https?:\/\//i.test(url)) {
      showToast('Enter a link starting with http:// or https://', 'error');
      return;
    }
    onChange((prev) => [
      ...prev,
      {
        // The author's label is what the card shows. Falling back to the URL's last
        // segment keeps the card from being nameless when the field is left blank.
        file_name: linkLabel.trim() || nameFromUrl(url),
        is_link: true,
        file_path: null,
        mime_type: null,
        public_url: url,
        media_type: kindFromUrl(url),
        file_size: null,
        // Set locally so the tile previews straight away; the server echoes public_url
        // back as `url` on every read, since there is nothing of ours to sign.
        url
      }
    ]);
    setLinkUrl('');
    setLinkLabel('');
  };

  // Only images and videos that actually resolved to a URL can be opened. The tile's
  // position in `value` is not its position in the viewer, so map between them.
  const isViewable = (attachment: AnnouncementAttachment) =>
    (attachment.media_type === 'image' || attachment.media_type === 'video') && Boolean(previewSrc(attachment));

  const viewable = value.filter(isViewable);
  const viewerItems: ViewerItem[] = viewable.map((attachment) => ({
    src: previewSrc(attachment),
    caption: attachment.file_name,
    type: attachment.media_type === 'video' ? 'video' : 'image'
  }));

  const renderThumbnail = (attachment: AnnouncementAttachment) => {
    const preview = previewSrc(attachment);
    if (attachment.media_type === 'image' && preview) {
      return <img src={preview} alt={attachment.file_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    }
    const Icon =
      attachment.media_type === 'video'
        ? PlayCircleOutlineIcon
        : attachment.media_type === 'image'
        ? ImageOutlinedIcon
        : InsertDriveFileOutlinedIcon;

    // A document tile is otherwise a bare icon with nothing to tell two PDFs apart, so
    // show the name under it — images and videos identify themselves by their thumbnail.
    return (
      <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ px: 0.5, width: '100%' }}>
        <Icon sx={{ fontSize: 28, color: 'text.secondary' }} />
        {attachment.media_type === 'file' && attachment.file_name && (
          <Typography
            variant="caption"
            sx={{
              width: '100%',
              textAlign: 'center',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 10
            }}
          >
            {attachment.file_name}
          </Typography>
        )}
      </Stack>
    );
  };

  return (
    <Stack spacing={1.5}>
      <Box
        {...getRootProps()}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive ? 'primary.main' : 'divider',
          bgcolor: isDragActive ? 'action.hover' : 'background.paper',
          borderRadius: 2,
          p: 2.5,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'all 0.2s ease',
          '&:hover': disabled ? {} : { borderColor: 'primary.main' }
        }}
      >
        <input {...getInputProps()} />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {isDragActive ? 'Drop the files here…' : 'Drag & drop files here'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      </Box>

      {/* Or point at something already hosted elsewhere. Nothing is uploaded — the address
          is stored as-is, and the label is what the attachment card shows. */}
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <TextField
          size="small"
          fullWidth
          label="Or paste a link"
          placeholder="https://…"
          value={linkUrl}
          disabled={disabled}
          onChange={(event) => setLinkUrl(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              // Otherwise Enter submits the surrounding announcement form.
              event.preventDefault();
              addLink();
            }
          }}
        />
        <TextField
          size="small"
          label="Label"
          placeholder="Release notes"
          value={linkLabel}
          disabled={disabled}
          onChange={(event) => setLinkLabel(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addLink();
            }
          }}
          sx={{ minWidth: 170, flexShrink: 0 }}
        />
        <Button variant="outlined" onClick={addLink} disabled={disabled || !linkUrl.trim()} sx={{ flexShrink: 0, height: 40 }}>
          Add
        </Button>
      </Stack>

      {(value.length > 0 || pending.length > 0) && (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {value.map((attachment, index) => (
            <Box
              key={`${attachment.file_path}-${index}`}
              onClick={() => {
                const position = viewable.indexOf(attachment);
                if (position > -1) setViewerIndex(position);
              }}
              sx={{
                position: 'relative',
                width: TILE,
                height: TILE,
                borderRadius: 1.5,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50',
                cursor: isViewable(attachment) ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={
                attachment.is_link
                  ? attachment.public_url ?? attachment.file_name
                  : isViewable(attachment)
                  ? `${attachment.file_name} — click to view`
                  : attachment.file_name
              }
            >
              {renderThumbnail(attachment)}
              {attachment.is_link && (
                // Marks the tile as a pointer to something we don't host, so a broken
                // link later is traceable to what it is.
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 2,
                    left: 2,
                    px: 0.4,
                    borderRadius: 0.75,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <LinkIcon sx={{ fontSize: 13 }} />
                </Box>
              )}
              {!disabled && (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    // Otherwise removing a tile also opens it in the viewer.
                    event.stopPropagation();
                    removeAt(index);
                  }}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    bgcolor: 'rgba(0,0,0,0.55)',
                    color: '#fff',
                    width: 20,
                    height: 20,
                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' }
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
          ))}

          {pending.map((name, index) => (
            <Box
              key={`pending-${name}-${index}`}
              sx={{
                width: TILE,
                height: TILE,
                borderRadius: 1.5,
                border: '1px dashed',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              title={name}
            >
              <CircularProgress size={22} />
            </Box>
          ))}
        </Stack>
      )}

      <MediaViewer
        open={viewerIndex !== null}
        items={viewerItems}
        index={viewerIndex ?? 0}
        onIndexChange={setViewerIndex}
        onClose={() => setViewerIndex(null)}
      />
    </Stack>
  );
};

export default AttachmentUploader;
